/**
 * LoginForm Component
 * 
 * A reusable login form component with email/password fields,
 * "Remember Me" checkbox, social login options, and theme toggle.
 * 
 * @example
 * ```typescript
 * const loginForm = new LoginForm(container, {
 *   onSuccess: (user, rememberMe) => {
 *     console.log('Logged in:', user.email, 'Remember:', rememberMe);
 *   },
 *   onForgotPassword: () => showForgotPasswordForm(),
 *   onSignup: () => showSignupForm(),
 *   showRememberMe: true,
 *   showSocialLogin: true
 * });
 * await loginForm.initialize();
 * ```
 */

import { IComponent } from '../IComponent';
import { LoginFormOptions, AuthUser, IAuthService } from './AuthTypes';
import { themeManager } from '@/services/ThemeManager';
import { ToasterService } from '@/services/ToasterService';
import { authService } from '@/services/Authentication';
import { auth0Service } from '@/services/Auth0Service';
import { oemConfig } from '@/services/OEMConfig';
import { AppInfo } from '@/services/AppInfo';
import { Logger } from '@/services/Logger';

const logger = Logger.scope('LoginForm');

export class LoginForm implements IComponent {
  private container: HTMLElement;
  private options: LoginFormOptions;
  private currentTheme: string = 'dark';
  private authSvc: IAuthService;

  constructor(container: HTMLElement, options: LoginFormOptions) {
    this.container = container;
    this.options = {
      showRememberMe: true,
      showSocialLogin: true,
      showThemeToggle: true,
      ...options
    };
    // Use custom auth service if provided, otherwise default
    this.authSvc = options.authService || authService;
  }

  async initialize(): Promise<void> {
    ToasterService.init();
    themeManager.init();
    this.currentTheme = themeManager.getCurrent();
    
    this.render();
    this.bindEvents();
    
    logger.debug('LoginForm initialized');
  }

  destroy(): void {
    // Remove event listeners by replacing container content
    this.container.innerHTML = '';
    logger.debug('LoginForm destroyed');
  }

  /**
   * Render the login form HTML
   */
  private render(): void {
    const company = oemConfig.getCompany();
    const oem = {
      loginLogo: oemConfig.getLoginLogo(),
      caption: oemConfig.getBrandName(),
      website: oemConfig.getWebsite(),
      loginBackground: oemConfig.getLoginBackground(),
      loginSubtitle: oemConfig.getBranding().loginSubtitle || '',
      loginTheme: oemConfig.getLoginTheme() || this.currentTheme,
      version: `${AppInfo.getVersion()}`,
      companyName: company.name,
      companyLogo: company.logo,
      companyWebsite: company.website,
      copyright: company.copyright
    };

    this.container.innerHTML = `
      <div class="login-form-container min-h-screen flex transition-all duration-1000" data-theme="${oem.loginTheme}">
        <!-- Left side - Background Image -->
        <div class="hidden lg:flex lg:w-1/2 relative bg-cover bg-center" style="background-image: url('${oem.loginBackground}');">
          <div class="flex flex-col justify-between w-full p-12">
            <!-- Logo and Version - Top Third -->
            <div class="flex-1 flex items-center justify-center">
              <div class="text-center">
                <a href="${oem.website}" target="_blank" rel="noopener noreferrer">
                  <img src="${oem.loginLogo}" alt="${oem.caption}" class="h-48 mx-auto mb-8 drop-shadow-2xl hover:opacity-90 transition-opacity">
                </a>
                ${oem.loginSubtitle ? `<p class="text-lg font-semibold drop-shadow-lg mb-2" style="color: #3B4151;">${oem.loginSubtitle}</p>` : ''}
                <p class="text-sm text-gray-800 font-semibold drop-shadow-lg">${oem.version}</p>
              </div>
            </div>

            <!-- Company Details - Bottom Third -->
            <div class="flex-1 flex items-end justify-center pb-12">
              <div class="text-center">
                <a href="${oem.companyWebsite}" target="_blank" rel="noopener noreferrer" class="block mb-4">
                  <img src="${oem.companyLogo}" alt="${oem.companyName}" class="h-12 mx-auto drop-shadow-lg hover:opacity-90 transition-opacity">
                </a>
                <p class="text-sm text-gray-800 drop-shadow-lg">${oem.copyright}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Right side - Login Form -->
        <div class="w-full lg:w-1/2 flex items-center justify-center p-8 bg-base-100 transition-all duration-1000">
          <div class="w-full max-w-md">
            <!-- Mobile Logo -->
            <div class="lg:hidden text-center mb-16">
              <img src="${oem.loginLogo}" alt="${oem.caption}" class="h-20 mx-auto">
            </div>

            <!-- Welcome Header with Theme Toggle -->
            <div class="mb-8">
              <div class="flex items-start justify-between mb-2">
                <h2 class="text-3xl font-bold">Welcome back</h2>
                ${this.options.showThemeToggle ? `
                  <button id="login-theme-toggle" class="btn btn-ghost btn-sm btn-circle tooltip tooltip-bottom tooltip-end" data-tip="Switch theme" aria-label="Toggle theme">
                    <i class="fas ${this.currentTheme === 'dark' ? 'fa-moon' : 'fa-sun text-yellow-400'} text-xl" id="login-theme-icon"></i>
                  </button>
                ` : ''}
              </div>
              <p class="text-base-content/70">Sign in to continue to ${oem.caption}</p>
            </div>

            <!-- Login Form -->
            <form id="login-form-component" class="space-y-6">
              <!-- Email -->
              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">Email</span>
                </label>
                <input 
                  type="email" 
                  id="login-email-input"
                  name="email" 
                  autocomplete="email"
                  placeholder="Enter your email" 
                  class="input input-bordered w-full input-lg" 
                  value="${this.options.defaultEmail || ''}"
                  required 
                />
              </div>

              <!-- Password -->
              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">Password</span>
                </label>
                <input 
                  type="password" 
                  id="login-password-input"
                  name="password" 
                  autocomplete="current-password"
                  placeholder="Enter your password" 
                  class="input input-bordered w-full input-lg" 
                  required 
                />
              </div>

              <!-- Remember Me & Forgot Password -->
              <div class="flex items-center justify-between">
                ${this.options.showRememberMe ? `
                  <label class="label cursor-pointer gap-2">
                    <input type="checkbox" id="login-remember-me" name="remember" class="checkbox checkbox-primary checkbox-sm" />
                    <span class="label-text">Remember me</span>
                  </label>
                ` : '<div></div>'}
                ${this.options.onForgotPassword ? `
                  <a role="button" id="login-forgot-password-link" class="link link-primary text-sm cursor-pointer">Forgot?</a>
                ` : ''}
              </div>

              <!-- Submit Button -->
              <button type="submit" id="login-submit-btn" class="btn btn-primary w-full btn-lg">
                Sign In
              </button>

              ${this.options.showSocialLogin ? `
                <!-- Divider -->
                <div class="divider">OR</div>

                <!-- Social Login Button -->
                <button type="button" id="login-social-btn" class="btn btn-outline w-full btn-lg">
                  <i class="fas fa-users"></i>
                  Continue with Social Account
                </button>
              ` : ''}

              ${this.options.onSignup ? `
                <!-- Sign Up Link -->
                <div class="text-center text-sm mt-6">
                  <p class="text-base-content/70">
                    Don't have an account? <a role="button" id="login-signup-link" class="link link-primary font-semibold cursor-pointer">Create Account</a>
                  </p>
                </div>
              ` : ''}
            </form>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Bind event listeners
   */
  private bindEvents(): void {
    const form = this.container.querySelector('#login-form-component') as HTMLFormElement;
    const themeToggle = this.container.querySelector('#login-theme-toggle');
    const forgotLink = this.container.querySelector('#login-forgot-password-link');
    const signupLink = this.container.querySelector('#login-signup-link');
    const socialBtn = this.container.querySelector('#login-social-btn');

    // Form submission
    form?.addEventListener('submit', (e) => this.handleSubmit(e));

    // Theme toggle
    themeToggle?.addEventListener('click', () => this.toggleTheme());

    // Forgot password link
    forgotLink?.addEventListener('click', (e) => {
      e.preventDefault();
      this.options.onForgotPassword?.();
    });

    // Signup link
    signupLink?.addEventListener('click', (e) => {
      e.preventDefault();
      this.options.onSignup?.();
    });

    // Social login button
    socialBtn?.addEventListener('click', () => this.handleSocialLogin());
  }

  /**
   * Handle social login via Auth0
   */
  private async handleSocialLogin(): Promise<void> {
    const socialBtn = this.container.querySelector('#login-social-btn') as HTMLButtonElement;
    const originalContent = socialBtn?.innerHTML;

    try {
      // Show loading state
      if (socialBtn) {
        socialBtn.disabled = true;
        socialBtn.innerHTML = '<span class="loading loading-spinner"></span> Connecting...';
      }

      // Call Auth0 service
      const auth0User = await auth0Service.login();

      // Show progress - now exchanging with backend
      ToasterService.info('Authenticating with server...');

      // Exchange Auth0 token with backend API to get full CRM profile
      const result = await this.authSvc.loginWithAuth0(auth0User.email, auth0User);

      if (result.success && result.user) {
        ToasterService.dismissAll();
        const providerName = auth0Service.getProviderName(auth0User.provider);
        ToasterService.success(`Welcome, ${result.user.name}!`);

        // Use the complete user object from authService (includes jobTitle, companyName)
        await new Promise(resolve => setTimeout(resolve, 300));
        this.options.onSuccess(result.user, false);  // Social login doesn't use "remember me"
      } else {
        ToasterService.error(result.message || 'Login failed. Please try again.');
        // Restore button
        if (socialBtn && originalContent) {
          socialBtn.disabled = false;
          socialBtn.innerHTML = originalContent;
        }
      }

    } catch (error) {
      logger.error('Social login failed:', error);
      
      // Check if user cancelled
      if (error instanceof Error && error.message.includes('cancelled')) {
        ToasterService.info('Login cancelled');
      } else {
        ToasterService.error('Social login failed. Please try again.');
      }

      // Restore button
      if (socialBtn && originalContent) {
        socialBtn.disabled = false;
        socialBtn.innerHTML = originalContent;
      }
    }
  }

  /**
   * Handle form submission
   */
  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const rememberMe = this.container.querySelector('#login-remember-me') as HTMLInputElement;
    const rememberMeChecked = rememberMe?.checked ?? false;

    const submitBtn = this.container.querySelector('#login-submit-btn') as HTMLButtonElement;
    const originalText = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="loading loading-spinner"></span> Signing in...';

    ToasterService.info('Authenticating...');
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      const result = await this.authSvc.login({ email, password });

      if (result.success && result.user) {
        ToasterService.dismissAll();

        // Use the complete user object from authService (includes jobTitle, companyName)
        const user: AuthUser = result.user;

        // Store remember me preference
        if (rememberMeChecked) {
          localStorage.setItem('flexiva_remember_email', email);
        } else {
          localStorage.removeItem('flexiva_remember_email');
        }

        await new Promise(resolve => setTimeout(resolve, 300));
        this.options.onSuccess(user, rememberMeChecked);
      } else {
        ToasterService.error(result.message || 'Login failed. Please try again.');
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalText;
      }
    } catch (error) {
      logger.error('Login error:', error);
      ToasterService.error('Login failed. Please check your credentials and try again.');
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalText;
    }
  }

  /**
   * Toggle theme between light and dark
   */
  private toggleTheme(): void {
    this.currentTheme = this.currentTheme === 'dark' ? 'light' : 'dark';
    themeManager.apply(this.currentTheme);

    // Update container theme
    const formContainer = this.container.querySelector('.login-form-container');
    formContainer?.setAttribute('data-theme', this.currentTheme);

    // Update icon
    const icon = this.container.querySelector('#login-theme-icon');
    if (icon) {
      icon.className = this.currentTheme === 'dark' 
        ? 'fas fa-moon text-xl' 
        : 'fas fa-sun text-xl text-yellow-400';
    }

    // Update left side overlay
    this.updateLeftSideOverlay();
    
    logger.debug('Theme toggled to:', this.currentTheme);
  }

  /**
   * Update the left side overlay based on theme
   */
  private updateLeftSideOverlay(): void {
    const leftSide = this.container.querySelector('.lg\\:w-1\\/2.relative.bg-cover') as HTMLElement;
    if (!leftSide) return;

    let overlay = leftSide.querySelector('.theme-overlay') as HTMLElement;

    if (this.currentTheme === 'light') {
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'theme-overlay absolute inset-0 bg-black transition-opacity duration-1000';
        overlay.style.opacity = '0';
        leftSide.appendChild(overlay);
      }
      setTimeout(() => {
        overlay.style.opacity = '0.5';
      }, 10);
    } else {
      if (overlay) {
        overlay.style.opacity = '0';
        setTimeout(() => overlay.remove(), 1000);
      }
    }
  }
}
